import * as React from "react";

/**
 * ThemeProvider — light / dark / system theme cascade.
 *
 * Sets the `dark` (or `light`) class on `<html>` so the consumer's CSS variables
 * cascade through every primitive that reads from `bg-background`, `bg-primary`,
 * etc. Persists the user's preference in `localStorage` and follows the OS
 * `prefers-color-scheme` when `theme` is `"system"`.
 *
 * Usage:
 *
 *   <ThemeProvider defaultTheme="system" storageKey="aisquare-theme">
 *     <App />
 *   </ThemeProvider>
 *
 *   const { theme, resolvedTheme, setTheme } = useTheme();
 *   <Button onClick={() => setTheme(resolvedTheme === "dark" ? "light" : "dark")}>
 *     Switch
 *   </Button>
 *
 * SSR / FOUC: the provider reads `localStorage` lazily, so first paint matches
 * `defaultTheme`. For zero-flash apps (Next.js, Remix), inline `getThemeScript()`
 * in `<head>` so the class is set before React hydrates.
 *
 * Composition: `ThemeProvider` is global (light/dark on `<html>`); `StudioBrand`
 * is scoped (per-studio brand vars on a wrapper). Both compose freely.
 */

export type Theme = "light" | "dark" | "system";
export type ResolvedTheme = "light" | "dark";

export interface ThemeProviderProps {
  children: React.ReactNode;
  /** Theme used until a stored preference loads. Defaults to `"system"`. */
  defaultTheme?: Theme;
  /** `localStorage` key for persistence. Defaults to `"aisquare-theme"`. */
  storageKey?: string;
  /** How to apply the theme to `<html>`. `"class"` adds `light`/`dark`; `"data-theme"` sets the attribute. */
  attribute?: "class" | "data-theme";
  /** When `true`, ignores `prefers-color-scheme` and treats `"system"` as `"light"`. */
  disableSystem?: boolean;
}

export interface ThemeContextValue {
  /** The user's selection (may be `"system"`). */
  theme: Theme;
  /** The active theme after resolving `"system"`. Always `"light"` or `"dark"`. */
  resolvedTheme: ResolvedTheme;
  /** Update the theme. Persists to `localStorage`. */
  setTheme: (theme: Theme) => void;
}

const ThemeContext = React.createContext<ThemeContextValue | undefined>(undefined);

const useIsomorphicLayoutEffect =
  typeof window !== "undefined" ? React.useLayoutEffect : React.useEffect;

function readStoredTheme(storageKey: string): Theme | null {
  if (typeof window === "undefined") return null;
  try {
    const value = window.localStorage.getItem(storageKey);
    if (value === "light" || value === "dark" || value === "system") return value;
    return null;
  } catch {
    return null;
  }
}

function systemPrefersDark(): boolean {
  if (typeof window === "undefined" || !window.matchMedia) return false;
  return window.matchMedia("(prefers-color-scheme: dark)").matches;
}

function resolve(theme: Theme, disableSystem: boolean): ResolvedTheme {
  if (theme === "system") return disableSystem ? "light" : systemPrefersDark() ? "dark" : "light";
  return theme;
}

function applyTheme(resolved: ResolvedTheme, attribute: "class" | "data-theme"): void {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  if (attribute === "class") {
    root.classList.remove("light", "dark");
    root.classList.add(resolved);
  } else {
    root.setAttribute("data-theme", resolved);
  }
}

export function ThemeProvider({
  children,
  defaultTheme = "system",
  storageKey = "aisquare-theme",
  attribute = "class",
  disableSystem = false,
}: ThemeProviderProps) {
  const [theme, setThemeState] = React.useState<Theme>(defaultTheme);
  const [resolvedTheme, setResolvedTheme] = React.useState<ResolvedTheme>(() =>
    resolve(defaultTheme, disableSystem),
  );

  useIsomorphicLayoutEffect(() => {
    const stored = readStoredTheme(storageKey);
    const initial = stored ?? defaultTheme;
    const resolved = resolve(initial, disableSystem);
    setThemeState(initial);
    setResolvedTheme(resolved);
    applyTheme(resolved, attribute);
  }, [storageKey, defaultTheme, disableSystem, attribute]);

  React.useEffect(() => {
    if (
      disableSystem ||
      theme !== "system" ||
      typeof window === "undefined" ||
      !window.matchMedia
    ) {
      return;
    }
    const media = window.matchMedia("(prefers-color-scheme: dark)");
    const handler = (event: MediaQueryListEvent) => {
      const next: ResolvedTheme = event.matches ? "dark" : "light";
      setResolvedTheme(next);
      applyTheme(next, attribute);
    };
    media.addEventListener("change", handler);
    return () => media.removeEventListener("change", handler);
  }, [theme, attribute, disableSystem]);

  const setTheme = React.useCallback(
    (next: Theme) => {
      if (typeof window !== "undefined") {
        try {
          window.localStorage.setItem(storageKey, next);
        } catch {
          // Storage may be unavailable (private mode, quota, sandboxed iframe). Ignore.
        }
      }
      const resolved = resolve(next, disableSystem);
      setThemeState(next);
      setResolvedTheme(resolved);
      applyTheme(resolved, attribute);
    },
    [storageKey, attribute, disableSystem],
  );

  const value = React.useMemo<ThemeContextValue>(
    () => ({ theme, resolvedTheme, setTheme }),
    [theme, resolvedTheme, setTheme],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

ThemeProvider.displayName = "ThemeProvider";

/**
 * Read the current theme + setter. Must be called inside a `<ThemeProvider>`.
 */
export function useTheme(): ThemeContextValue {
  const ctx = React.useContext(ThemeContext);
  if (!ctx) {
    throw new Error("useTheme must be used inside a ThemeProvider.");
  }
  return ctx;
}

/**
 * Inline this script in `<head>` (Next.js / Remix / Astro) to set the theme
 * class before React hydrates and avoid a flash:
 *
 *   <head>
 *     <script dangerouslySetInnerHTML={{ __html: getThemeScript() }} />
 *   </head>
 */
export function getThemeScript(
  storageKey: string = "aisquare-theme",
  defaultTheme: Theme = "system",
  attribute: "class" | "data-theme" = "class",
): string {
  return `(function(){try{var t=localStorage.getItem(${JSON.stringify(storageKey)});if(t!=="light"&&t!=="dark"&&t!=="system")t=${JSON.stringify(defaultTheme)};var d=t==="dark"||(t==="system"&&matchMedia("(prefers-color-scheme: dark)").matches);var r=document.documentElement;${
    attribute === "class"
      ? `r.classList.remove("light","dark");r.classList.add(d?"dark":"light");`
      : `r.setAttribute("data-theme",d?"dark":"light");`
  }}catch(e){}})();`;
}
